import React, { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { api, colones, todayISO } from "@/lib/api";
import { toast } from "sonner";
import {
  Receipt, Plus, ArrowLeft, Save, Download, Trash2, User, Calendar, Search,
} from "lucide-react";
import {
  MOTIVOS_LIQUIDACION, getMotivo, calcularLiquidacion, salariosMensualesLive,
  diasPreaviso, diasCesantia, tiempoLaborado,
} from "@/lib/liquidacion";
import { computePayrollForEmployee } from "@/lib/payroll";
import { buildPuestosMap, puestoNoHorasExtras } from "@/lib/puestos";
import { generarLiquidacionPDF } from "@/lib/liquidacionPDF";
import { calcularSaldoVacaciones } from "@/lib/vacaciones";

// ============================================================================
// LIST
// ============================================================================
function LiquidacionesList({ items, empleados, onNew, onDownload, onDelete }) {
  const [q, setQ] = useState("");
  const empMap = useMemo(() => {
    const m = {}; empleados.forEach(e => { m[e.id] = `${e.nombre} ${e.apellidos || ""}`.trim(); }); return m;
  }, [empleados]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter(it => (empMap[it.empleado_id] || "").toLowerCase().includes(s) || (it.motivo_label || "").toLowerCase().includes(s));
  }, [items, q, empMap]);

  return (
    <>
      <header className="flex items-end justify-between mb-6 flex-wrap gap-4 fade-in-up">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-[#E6EFEA] text-primary-color flex items-center justify-center"><Receipt size={26} strokeWidth={1.5} /></div>
          <div>
            <h1 className="font-display text-3xl font-semibold">Liquidaciones Laborales</h1>
            <p className="text-sm text-secondary-c">Cálculo conforme al Código de Trabajo de Costa Rica.</p>
          </div>
        </div>
        <button onClick={onNew} className="np-btn np-btn-primary" data-testid="liq-new">
          <Plus size={16} className="mr-1.5" /> Nueva Liquidación
        </button>
      </header>

      <div className="np-card p-3 mb-5">
        <div className="flex items-center gap-2 px-2">
          <Search size={16} className="text-secondary-c" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar por trabajador o motivo..."
            className="flex-1 bg-transparent outline-none text-sm py-2" data-testid="liq-search" />
          {items.length > 0 && <span className="text-xs text-secondary-c whitespace-nowrap">{filtered.length} de {items.length}</span>}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="np-card p-14 text-center text-secondary-c">
          <Receipt size={36} className="mx-auto mb-3 opacity-60" />
          Aún no hay liquidaciones generadas. Crea la primera.
        </div>
      ) : filtered.length === 0 ? (
        <div className="np-card p-10 text-center text-secondary-c">Sin resultados.</div>
      ) : (
        <div className="np-card overflow-hidden" data-testid="liq-list">
          <table className="np-table">
            <thead><tr><th>Trabajador</th><th>Motivo</th><th>Fecha de Salida</th><th>Total</th><th className="text-right">Acciones</th></tr></thead>
            <tbody>
              {filtered.map(it => (
                <tr key={it.id} data-testid={`liq-row-${it.id}`}>
                  <td><div className="font-medium">{empMap[it.empleado_id] || <em className="text-secondary-c">Sin trabajador</em>}</div></td>
                  <td className="text-secondary-c text-xs">{it.motivo_label}</td>
                  <td>{it.fecha_salida ? new Date(`${it.fecha_salida.slice(0,10)}T00:00:00`).toLocaleDateString("es-CR") : "—"}</td>
                  <td className="font-semibold">{colones(it.total)}</td>
                  <td>
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => onDownload(it.id)} className="np-btn np-btn-outline p-2" title="Descargar PDF" data-testid={`liq-download-${it.id}`}><Download size={14} /></button>
                      <button onClick={() => onDelete(it)} className="np-btn np-btn-ghost p-2 text-error hover:bg-[#FDEDED]" title="Eliminar" data-testid={`liq-delete-${it.id}`}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ============================================================================
// FORM
// ============================================================================
// Input monetario que muestra "₡ 570.000,00" cuando pierde foco y el número
// crudo editable cuando tiene foco. Guarda como Number en el state parent.
function MoneyCell({ value, disabled, onChange, testId, className = "" }) {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState(String(Number(value) || 0));
  useEffect(() => {
    // sync desde parent cuando no está en edición
    if (!focused) setDraft(String(Number(value) || 0));
  }, [value, focused]);
  const display = focused ? draft : colones(Number(value) || 0);
  return (
    <input
      type="text"
      inputMode="decimal"
      value={display}
      disabled={disabled}
      onFocus={() => { setFocused(true); setDraft(String(Number(value) || 0)); }}
      onChange={(e) => setDraft(e.target.value.replace(/[^\d.,-]/g, ""))}
      onBlur={() => {
        setFocused(false);
        const parsed = Number(String(draft).replace(/\./g, "").replace(/,/g, ".")) || 0;
        onChange(parsed);
      }}
      className={`np-input text-xs py-1 px-2 text-right w-32 ${className}`}
      data-testid={testId}
    />
  );
}

function LiquidacionForm({ empleados, empresa, asistencias, rebajos, bonificaciones, overrides, onBack, onSaved }) {
  const [empleadoId, setEmpleadoId] = useState("");
  const [motivoId, setMotivoId] = useState(MOTIVOS_LIQUIDACION[0].id);
  const [fechaSalida, setFechaSalida] = useState(() => new Date().toISOString().slice(0, 10));
  const [diasVacacionesPendientes, setDiasVacacionesPendientes] = useState(0);
  const [adelanto, setAdelanto] = useState(0);
  const [ofertaPatrono, setOfertaPatrono] = useState(0);
  const [salarios, setSalarios] = useState([]);
  const [toggles, setToggles] = useState({});  const [saving, setSaving] = useState(false);

  const empleado = useMemo(() => empleados.find(e => e.id === empleadoId), [empleadoId, empleados]);
  const motivo = useMemo(() => getMotivo(motivoId), [motivoId]);
  const puestosMap = useMemo(() => buildPuestosMap(empresa), [empresa]);

  // Cuando cambia empleado o fecha de salida: recalcular salarios (live) y vacaciones pendientes.
  // Los meses previos a fecha_ingreso quedan en 0 y no editables.
  useEffect(() => {
    if (!empleado) return;
    const sal = salariosMensualesLive({
      empleado, empresa,
      allAsistencia: asistencias, allRebajos: rebajos, allBonif: bonificaciones, allOverrides: overrides,
      fechaSalida, computePayrollForEmployee, puestosMap, puestoNoHorasExtras,
    });
    setSalarios(sal);
    const saldo = calcularSaldoVacaciones(empleado, asistencias || [], new Date(`${fechaSalida}T00:00:00`), empresa);
    setDiasVacacionesPendientes(Math.max(0, saldo.saldo || 0));
  }, [empleadoId, fechaSalida, empleado, empresa, asistencias, rebajos, bonificaciones, overrides, puestosMap]);

  // Re-set toggles when motivo changes (defaults to motivo's rules)
  useEffect(() => { setToggles({}); }, [motivoId]);

  const calc = useMemo(() => {
    if (!empleado || !salarios.length) return null;
    return calcularLiquidacion({
      empleado, empresa, fechaSalida, motivoId, salariosMensuales: salarios,
      diasVacacionesPendientes: Number(diasVacacionesPendientes) || 0,
      adelanto: Number(adelanto) || 0, toggles,
    });
  }, [empleado, empresa, fechaSalida, motivoId, salarios, diasVacacionesPendientes, adelanto, toggles]);

  const updateSalario = (idx, field, val) => {
    setSalarios(prev => prev.map((s, i) => i === idx ? { ...s, [field]: Number(val) || 0 } : s));
  };
  const setTog = (rubro, value) => setToggles(prev => ({ ...prev, [rubro]: value }));

  const renderToggle = (rubro, label, monto, disabled = false) => {
    const checked = calc?.incluir?.[rubro] ?? false;
    return (
      <label key={rubro} className={`flex items-center justify-between gap-3 p-3 rounded-xl border ${checked ? "border-primary-c bg-[#E6EFEA]" : "border-c bg-white"} ${disabled ? "opacity-60" : ""}`}>
        <div className="flex items-center gap-2.5">
          <input type="checkbox" checked={checked} disabled={disabled} onChange={e => setTog(rubro, e.target.checked)}
            className="w-4 h-4 accent-[#274735]" data-testid={`liq-toggle-${rubro}`} />
          <span className="text-sm font-medium">{label}</span>
        </div>
        <span className="font-semibold text-sm" data-testid={`liq-monto-${rubro}`}>{colones(monto)}</span>
      </label>
    );
  };

  const guardar = async () => {
    if (!empleado || !calc) { toast.error("Selecciona un trabajador y completa los datos"); return; }
    setSaving(true);
    try {
      const noAcc = `${empleado.nombre}_${empleado.apellidos || ""}`
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      const safeName = noAcc.replace(/[^A-Za-z0-9_\-]+/g, "_");
      const file = `Liquidacion_${safeName}_${fechaSalida}.pdf`;
      const result = await generarLiquidacionPDF({
        empresa, empleado, fechaSalida, motivo, calc, salarios,
        diasPreavisoUsados: calc.incluir.preaviso ? calc.dPreaviso : 0,
        diasCesantiaUsados: calc.incluir.cesantia ? calc.dCesantia : 0,
        elaboradoPor: empresa?.razon_social || empresa?.nombre || "",
        fechaDocumento: todayISO(),
        ofertaPatrono: Number(ofertaPatrono) || 0,
        fileName: file,
      });
      const payload = {
        empleado_id: empleado.id,
        motivo_id: motivoId,
        motivo_label: motivo.label,
        fecha_salida: fechaSalida,
        salarios_mensuales: salarios,
        dias_vacaciones_pendientes: diasVacacionesPendientes,
        adelanto: Number(adelanto) || 0,
        oferta_patrono: Number(ofertaPatrono) || 0,
        total: calc.totalLiquidacion,
        diferencia: calc.diferencia,
        montos: calc.montos,
        incluir: calc.incluir,
        dias_preaviso: calc.incluir.preaviso ? calc.dPreaviso : 0,
        dias_cesantia: calc.incluir.cesantia ? calc.dCesantia : 0,
        promedios: { p6: calc.prom6, pd6: calc.promDiario6, p50: calc.prom50, pd50: calc.promDiario50 },
        file_name: result.fileName,
        pdf_base64: result.pdfBase64,
      };
      await api.post("/liquidaciones", payload);
      toast.success("Liquidación generada y archivada");
      onSaved?.();
    } catch (e) {
      console.error(e);
      toast.error("No se pudo generar la liquidación");
    } finally { setSaving(false); }
  };

  return (
    <>
      <header className="flex items-center gap-3 mb-6 fade-in-up">
        <button onClick={onBack} className="np-btn np-btn-ghost p-2" data-testid="liq-back"><ArrowLeft size={18} /></button>
        <div>
          <h1 className="font-display text-3xl font-semibold leading-tight">Nueva Liquidación</h1>
          <p className="text-sm text-secondary-c">Selecciona el trabajador y completa los datos del cálculo.</p>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-5">
        {/* Sidebar: Datos básicos */}
        <div className="space-y-4">
          <div className="np-card p-5 space-y-3">
            <label className="text-sm font-medium flex items-center gap-2"><User size={15} /> Trabajador</label>
            <select value={empleadoId} onChange={e => setEmpleadoId(e.target.value)} className="np-input w-full" data-testid="liq-empleado">
              <option value="">Selecciona un trabajador...</option>
              {empleados.map(e => <option key={e.id} value={e.id}>{e.nombre} {e.apellidos} — {e.cedula}</option>)}
            </select>
            {empleado && (
              <div className="text-xs text-secondary-c space-y-0.5 pt-2 border-t border-c">
                <div><strong>Puesto:</strong> {empleado.puesto || "—"}</div>
                <div><strong>Ingreso:</strong> {empleado.fecha_ingreso || "—"}</div>
                <div><strong>Salario base:</strong> {colones(empleado.salario_base)}</div>
              </div>
            )}
          </div>

          <div className="np-card p-5 space-y-3">
            <label className="text-sm font-medium flex items-center gap-2"><Calendar size={15} /> Fecha de Salida</label>
            <input type="date" value={fechaSalida} onChange={e => setFechaSalida(e.target.value)} className="np-input w-full" data-testid="liq-fecha-salida" />
            {empleado && calc && (
              <div className="text-xs text-secondary-c pt-2 border-t border-c">
                <strong>Tiempo laborado:</strong> {calc.tiempo.anios} años, {calc.tiempo.mesesRestantes} meses
              </div>
            )}
          </div>

          <div className="np-card p-5 space-y-3">
            <label className="text-sm font-medium">Motivo de finalización</label>
            <select value={motivoId} onChange={e => setMotivoId(e.target.value)} className="np-input w-full" data-testid="liq-motivo">
              {MOTIVOS_LIQUIDACION.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
            <div className="text-xs text-secondary-c italic">{motivo.articulo}</div>
          </div>

          <div className="np-card p-5 space-y-3">
            <label className="text-sm font-medium">Días de Vacaciones Pendientes</label>
            <input type="number" min="0" step="0.5" value={diasVacacionesPendientes}
              onChange={e => setDiasVacacionesPendientes(Number(e.target.value))} className="np-input w-full" data-testid="liq-dias-vac" />
            <p className="text-xs text-secondary-c">Auto-calculado desde el módulo Vacaciones. Editable.</p>
          </div>

          <div className="np-card p-5 space-y-3">
            <label className="text-sm font-medium">Adelanto / Pagado anticipado</label>
            <input type="number" min="0" step="100" value={adelanto}
              onChange={e => setAdelanto(Number(e.target.value))} className="np-input w-full" data-testid="liq-adelanto" />
            <p className="text-xs text-secondary-c">Se resta del total para calcular la diferencia.</p>
          </div>

          <div className="np-card p-5 space-y-3">
            <label className="text-sm font-medium">Oferta del patrono <span className="text-xs text-secondary-c font-normal">(opcional)</span></label>
            <input type="number" min="0" step="100" value={ofertaPatrono}
              onChange={e => setOfertaPatrono(Number(e.target.value))} className="np-input w-full" data-testid="liq-oferta-patrono" placeholder="0" />
            <p className="text-xs text-secondary-c">Compara con el mínimo legal y advierte si está por debajo.</p>
          </div>
        </div>

        {/* Main: cálculos + desglose */}
        <div className="space-y-4">
          {!empleado ? (
            <div className="np-card p-12 text-center text-secondary-c">Selecciona un trabajador para iniciar el cálculo.</div>
          ) : !calc ? (
            <div className="np-card p-12 text-center text-secondary-c">Cargando…</div>
          ) : (
            <>
              {/* Rubros */}
              <div className="np-card p-5">
                <div className="text-sm font-semibold uppercase tracking-widest text-secondary-c mb-3">Rubros a Pagar</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                  {renderToggle("aguinaldo", "Aguinaldo proporcional", calc.montos.aguinaldo)}
                  {renderToggle("vacaciones", `Vacaciones (${calc.diasVacacionesPendientes} d)`, calc.montos.vacaciones)}
                  {renderToggle("preaviso", `Preaviso (${calc.incluir.preaviso ? calc.dPreaviso : diasPreaviso(calc.tiempo.meses)} d)`, calc.montos.preaviso)}
                  {renderToggle("cesantia", `Cesantía (${calc.incluir.cesantia ? calc.dCesantia : diasCesantia(calc.tiempo.meses)} d)`, calc.montos.cesantia)}
                </div>
              </div>

              {/* Totales */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="np-card p-4 bg-[#E6EFEA] border-primary-c">
                  <div className="text-xs uppercase tracking-widest text-secondary-c font-semibold">Total Liquidación</div>
                  <div className="font-display text-2xl font-semibold mt-1" data-testid="liq-total">{colones(calc.totalLiquidacion)}</div>
                </div>
                <div className="np-card p-4">
                  <div className="text-xs uppercase tracking-widest text-secondary-c font-semibold">Adelanto</div>
                  <div className="font-display text-xl font-medium mt-1 text-secondary-c">{colones(calc.adelanto)}</div>
                </div>
                <div className="np-card p-4 bg-[#FFF4E5]">
                  <div className="text-xs uppercase tracking-widest text-secondary-c font-semibold">Diferencia a Pagar</div>
                  <div className="font-display text-2xl font-semibold mt-1" data-testid="liq-diferencia">{colones(calc.diferencia)}</div>
                </div>
              </div>

              {/* Comparativo oferta vs. mínimo legal */}
              {ofertaPatrono > 0 && (() => {
                const dif = Number(ofertaPatrono) - calc.totalLiquidacion;
                const cumple = dif >= 0;
                return (
                  <div
                    className={`np-card p-5 border-2 ${cumple ? "border-success bg-[#E8F5EE]" : "border-error bg-[#FDEDED]"}`}
                    data-testid="liq-oferta-comparativo"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${cumple ? "bg-success" : "bg-error"} text-white font-bold`}>
                        {cumple ? "✓" : "!"}
                      </div>
                      <div className="flex-1">
                        <div className={`font-display text-lg font-semibold ${cumple ? "text-success" : "text-error"}`} data-testid="liq-oferta-veredicto">
                          {cumple ? "La oferta cumple con el mínimo legal" : "Oferta por debajo del mínimo legal"}
                        </div>
                        <div className="text-sm text-secondary-c mt-1">
                          Mínimo legal: <strong>{colones(calc.totalLiquidacion)}</strong> · Oferta del patrono: <strong>{colones(ofertaPatrono)}</strong>
                        </div>
                        <div className={`mt-2 text-sm font-semibold ${cumple ? "text-success" : "text-error"}`} data-testid="liq-oferta-diferencia">
                          {cumple
                            ? `Excedente a favor del trabajador: ${colones(dif)}`
                            : `Faltante por pagar: ${colones(Math.abs(dif))}`}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Promedios */}
              <div className="np-card p-5">
                <div className="text-sm font-semibold uppercase tracking-widest text-secondary-c mb-3">Promedios Salariales</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div><div className="text-xs text-secondary-c">Prom. 6 meses</div><div className="font-medium" data-testid="liq-prom6">{colones(calc.prom6)}</div></div>
                  <div><div className="text-xs text-secondary-c">Diario 6 m</div><div className="font-medium">{colones(calc.promDiario6)}</div></div>
                  <div><div className="text-xs text-secondary-c">Prom. 50 sem</div><div className="font-medium">{colones(calc.prom50)}</div></div>
                  <div><div className="text-xs text-secondary-c">Diario 50 sem</div><div className="font-medium">{colones(calc.promDiario50)}</div></div>
                </div>
              </div>

              {/* Desglose de salarios editable */}
              <div className="np-card p-5">
                <div className="text-sm font-semibold uppercase tracking-widest text-secondary-c mb-3">Desglose de Salarios (12 meses)</div>
                <div className="overflow-x-auto">
                  <table className="np-table text-xs">
                    <thead><tr><th>Año</th><th>Mes</th><th className="text-right">Salario</th><th className="text-right">En Especie</th></tr></thead>
                    <tbody data-testid="liq-salarios-table">
                      {salarios.map((s, i) => (
                        <tr key={`${s.year}-${s.label}`} className={s.disabled ? "opacity-50" : ""}>
                          <td>{s.year}</td>
                          <td>{s.label}</td>
                          <td>
                            <MoneyCell
                              value={s.salarios}
                              disabled={s.disabled}
                              onChange={(v) => updateSalario(i, "salarios", v)}
                              testId={`liq-sal-${i}`}
                            />
                          </td>
                          <td>
                            <MoneyCell
                              value={s.especie || 0}
                              disabled={s.disabled}
                              onChange={(v) => updateSalario(i, "especie", v)}
                              testId={`liq-esp-${i}`}
                            />
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-[#FAFAF7] font-semibold">
                        <td colSpan={2}>Total</td>
                        <td className="text-right">{colones(calc.totalSalarios)}</td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-secondary-c mt-2">Calculado en vivo desde asistencia, rebajos y bonificaciones (igual que Planillas Mensuales). Meses anteriores al ingreso del empleado quedan en 0 y no se pueden editar. Podés ajustar valores manualmente si es necesario.</p>
              </div>

              <button onClick={guardar} disabled={saving} className="np-btn np-btn-primary w-full justify-center" data-testid="liq-submit">
                <Save size={16} className="mr-1.5" /> {saving ? "Generando..." : "Generar y Guardar Liquidación"}
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ============================================================================
// ROOT
// ============================================================================
export default function Liquidaciones() {
  const [mode, setMode] = useState("list"); // list | form
  const [items, setItems] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [empresa, setEmpresa] = useState({});
  const [asistencias, setAsistencias] = useState([]);
  const [rebajos, setRebajos] = useState([]);
  const [bonificaciones, setBonificaciones] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [confirmDel, setConfirmDel] = useState(null);

  const reload = async () => {
    const r = await api.get("/liquidaciones");
    setItems(r.data || []);
  };

  useEffect(() => {
    (async () => {
      const [l, e, c, asis, reb, bon, ovr] = await Promise.all([
        api.get("/liquidaciones"),
        api.get("/empleados"),
        api.get("/empresa"),
        api.get("/asistencia"),
        api.get("/rebajos"),
        api.get("/bonificaciones"),
        api.get("/planilla-overrides"),
      ]);
      setItems(l.data || []);
      setEmpleados(e.data || []);
      setEmpresa(c.data || {});
      setAsistencias(asis.data || []);
      setRebajos(reb.data || []);
      setBonificaciones(bon.data || []);
      setOverrides(ovr.data || []);
    })();
  }, []);

  const descargar = async (id) => {
    try {
      const { data } = await api.get(`/liquidaciones/${id}`);
      if (!data?.pdf_base64) { toast.error("Sin PDF almacenado"); return; }
      const link = document.createElement("a");
      link.href = `data:application/pdf;base64,${data.pdf_base64}`;
      link.download = data.file_name || `Liquidacion_${id}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
    } catch { toast.error("No se pudo descargar"); }
  };

  const eliminar = async () => {
    if (!confirmDel) return;
    await api.delete(`/liquidaciones/${confirmDel.id}`);
    setConfirmDel(null);
    toast.success("Liquidación eliminada");
    await reload();
  };

  return (
    <Layout>
      {mode === "list" ? (
        <LiquidacionesList items={items} empleados={empleados}
          onNew={() => setMode("form")}
          onDownload={descargar}
          onDelete={(it) => setConfirmDel(it)} />
      ) : (
        <LiquidacionForm
          empleados={empleados} empresa={empresa}
          asistencias={asistencias} rebajos={rebajos} bonificaciones={bonificaciones} overrides={overrides}
          onBack={() => setMode("list")}
          onSaved={async () => { await reload(); setMode("list"); }} />
      )}

      {confirmDel && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setConfirmDel(null)}>
          <div className="np-card p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <h3 className="font-display text-lg font-semibold mb-2">Eliminar liquidación</h3>
            <p className="text-sm text-secondary-c mb-4">Esta acción no se puede deshacer.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDel(null)} className="np-btn np-btn-outline" data-testid="liq-confirm-cancel">Cancelar</button>
              <button onClick={eliminar} className="np-btn np-btn-primary !bg-error" data-testid="liq-confirm-delete">Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
